<template>
  <div class="role-and-right-assignment-container d-flex">
    <b-dropdown
      v-if="unassignedRoles.length > 0"
      ref="dropdownAssignRoles"
      class="mx-2 role-assignment-dropdown"
      menu-class="role-assignment-menu"
      variant="primary"
      right
      size="sm"
      :text="$getText('USERS_BUTTON_ADD_USER_ROLES')"
    >
      <b-form-input v-model="searchQuery" type="text" size="sm" :placeholder="$getText('SEARCH_LABEL')" />
      <b-form-checkbox-group v-model="selectedRoleIdsToAssign" stacked class="roles-list my-2">
        <template v-for="(userRole, i) in unassignedRoles">
          <b-form-checkbox
            v-show="getCanShowRole(userRole)"
            :key="i"
            :value="userRole.id"
            @change="onUserRoleChanged(userRole)"
          >
            {{ userRole.name }}
          </b-form-checkbox>
          <template v-for="(subRole, j) in getUnassignedSubRoles(userRole.subRoles)">
            <b-form-checkbox
              v-show="getCanShowRole(subRole)"
              :key="i + '-' + j"
              class="subRole"
              :value="subRole.id"
              @change="onSubRoleChanged(userRole)"
            >
              {{ subRole.name }}
            </b-form-checkbox>
          </template>
        </template>
      </b-form-checkbox-group>
      <div class="d-flex justify-content-center">
        <AulaButton variant="primary" :disabled="isLoading" class="action-btn" @click="onAssignRoles">
          <AulaSpinner v-if="isLoading" class="spinner" />
          {{ $getText('BUTTON_SAVE') }}
        </AulaButton>
      </div>
    </b-dropdown>
    <b-dropdown
      v-if="assignedRoles.length > 0"
      ref="dropdownRemoveRoles"
      menu-class="role-assignment-menu"
      variant="primary"
      right
      size="sm"
      :text="$getText('USERS_BUTTON_REMOVE_USER_ROLES')"
    >
      <b-form-input v-model="searchQuery" type="text" :placeholder="$getText('SEARCH_LABEL')" />
      <b-form-checkbox-group v-model="selectedRoleIdsToRemove" stacked class="roles-list my-2">
        <template v-for="(groupRole, i) in assignedRoles">
          <b-form-checkbox v-show="getCanShowRole(groupRole)" :key="i" :value="groupRole.id">
            {{ groupRole.name }}
          </b-form-checkbox>
        </template>
      </b-form-checkbox-group>
      <div class="d-flex justify-content-center">
        <AulaButton class="action-btn" variant="primary" :disabled="isLoading" @click="onRemoveRoles">
          <AulaSpinner v-if="isLoading" class="spinner" />
          {{ $getText('BUTTON_SAVE') }}
        </AulaButton>
      </div>
    </b-dropdown>
  </div>
</template>
<script>
import { providerKeyEnum } from '../enums/providerKeyEnum';
import AulaButton from './AulaButton.vue';
import { roleEnum } from '../enums/roleEnum';

export default {
  name: 'RoleAndRightAssignments',
  components: { AulaButton },
  inject: {
    getSelectedInstitution: providerKeyEnum.selectedInstitution,
    getRoleDefinitions: providerKeyEnum.roleDefinitions,
    getSelectedUserAssignedRoles: providerKeyEnum.selectedUserAssignedRoles,
    assignRolesToUser: providerKeyEnum.assignRolesToUser,
    getSelectedUsers: providerKeyEnum.getSelectedUsers,
  },
  data() {
    return {
      isLoading: false,
      searchQuery: '',
      selectedRoleIdsToAssign: [],
      selectedRoleIdsToRemove: [],
    };
  },
  computed: {
    selectedInstitution() {
      return this.getSelectedInstitution();
    },
    selectedUsers() {
      return this.getSelectedUsers();
    },
    canAssignEsdhRole() {
      // ESDH access: temporary institution whitelist.
      // Why: Needed until proper role/permission source is implemented.
      // Tracked in ADO #576890 — https://source.netcompany.com/tfs/NC02/KMTAULA/_workitems/edit/576890
      const whitelist = ['00000', '00003', '00006', '00012', '111111', 'A00943', 'A00969'];
      return (
        this.selectedUsers.length > 0 && this.selectedUsers.every(user => whitelist.includes(user.institutionCode))
      );
    },
    roleDefinitions() {
      return this.getRoleDefinitions();
    },
    assignedRoles() {
      return this.getSelectedUserAssignedRoles();
    },
    unassignedRoles() {
      const assignedRoleIds = this.assignedRoles.map(role => role.id);
      return this.roleDefinitions.filter(definition => {
        if (assignedRoleIds.includes(definition.id)) {
          return false;
        }
        return this.canAssignEsdhRole || [roleEnum.JOURNALING_TO_ESDH].includes(parseInt(definition.id)) === false;
      });
    },
  },
  methods: {
    getCanShowRole(role) {
      return !this.searchQuery || role.name.toLowerCase().includes(this.searchQuery.toLowerCase());
    },
    getUnassignedSubRoles(subRoles) {
      if (!subRoles) {
        return [];
      }
      const assignedRoleIds = this.assignedRoles.map(role => role.id);
      return subRoles.filter(subRole => {
        if (assignedRoleIds.includes(subRole.id)) {
          return false;
        }
        return (
          this.canAssignEsdhRole ||
          [roleEnum.HANDLE_FILE_SENT_TO_ESDH, roleEnum.HANDLE_MUNICIPAL_FILE_SENT_TO_ESDH].includes(
            parseInt(subRole.id)
          ) === false
        );
      });
    },
    onUserRoleChanged(userRole) {
      if (!userRole.subRoles) {
        return;
      }
      if (this.selectedRoleIdsToAssign.includes(userRole.id)) {
        userRole.subRoles.forEach(subRole => {
          if (!this.selectedRoleIdsToAssign.includes(subRole.id)) {
            this.selectedRoleIdsToAssign.push(subRole.id);
          }
        });
      } else {
        const subRoleIds = userRole.subRoles.map(subRole => subRole.id);
        this.selectedRoleIdsToAssign = this.selectedRoleIdsToAssign.filter(id => subRoleIds.includes(id) === false);
      }
    },
    onSubRoleChanged(userRole) {
      const assignableSubRoles = this.getUnassignedSubRoles(userRole.subRoles);
      if (assignableSubRoles.every(subRole => this.selectedRoleIdsToAssign.includes(subRole.id))) {
        this.selectedRoleIdsToAssign.push(userRole.id);
      } else {
        this.selectedRoleIdsToAssign = this.selectedRoleIdsToAssign.filter(roleId => userRole.id !== roleId);
      }
    },
    async onAssignRoles() {
      await this.setRoleAssignments();
      this.$refs.dropdownAssignRoles.visible = false;
    },
    async onRemoveRoles() {
      await this.setRoleAssignments();
      this.$refs.dropdownRemoveRoles.visible = false;
    },
    async setRoleAssignments() {
      this.isLoading = true;
      await this.assignRolesToUser({
        roleIdsToAssign: this.selectedRoleIdsToAssign,
        roleIdsToUnAssign: this.selectedRoleIdsToRemove,
      });
      this.isLoading = false;
      this.resetSelectedValues();
    },
    resetSelectedValues() {
      this.selectedRoleIdsToAssign = [];
      this.selectedRoleIdsToRemove = [];
    },
  },
};
</script>
<style scoped lang="scss">
input.form-control {
  --input-border-color: var(--color-grey-base);
}

.spinner {
  width: 50px;
  height: 50px;
  position: absolute;
  transform: translate(-25%, -25%);
}
</style>
